import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  LayoutDashboard, 
  PieChart, 
  DollarSign, 
  User,
  LogOut,
  Menu,
  X,
  Bell,
  Users
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/referrals", label: "Referrals", icon: Users },
  { path: "/analytics", label: "Analytics", icon: PieChart },
  { path: "/payments", label: "Payments", icon: DollarSign },
  { path: "/profile", label: "Profile", icon: User },
];

const Layout = ({ children, user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [ws, setWs] = useState(null);

  // WebSocket connection for real-time notifications
  useEffect(() => {
    if (user?.partner_id) {
      const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      const socket = new WebSocket(`${wsUrl}/ws/${user.partner_id}`);
      
      socket.onopen = () => {
        console.log('WebSocket connected');
      };
      
      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_referral') {
            toast.success(`New referral: ${data.name}! +$${data.commission}`, {
              duration: 5000,
            });
            setNotifications(prev => [data, ...prev].slice(0, 10));
          }
        } catch (e) {
          // Non-JSON message (ping/pong)
        }
      };
      
      socket.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
      socket.onclose = () => {
        console.log('WebSocket closed');
      };
      
      setWs(socket);
      
      return () => {
        socket.close();
      };
    }
  }, [user?.partner_id]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/', { replace: true });
    }
  };

  const NavContent = () => (
    <nav className="space-y-1">
      {navItems.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;
        
        return (
          <NavLink
            key={item.path}
            to={item.path}
            data-testid={`nav-${item.label.toLowerCase()}`}
            className={`flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
              isActive 
                ? "bg-purple-50 text-[#6B46C1]" 
                : "text-slate-600 hover:text-[#6B46C1] hover:bg-purple-50"
            }`}
            onClick={() => setMobileOpen(false)}
          >
            <Icon className="w-5 h-5" />
            {item.label}
          </NavLink>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 border-r border-slate-100 bg-white">
        <div className="flex flex-col flex-grow pt-5 pb-4 overflow-y-auto">
          {/* Logo */}
          <div className="flex items-center gap-2 px-6 mb-8">
            <div className="w-10 h-10 flex items-center justify-center">
              <img src="https://customer-assets.emergentagent.com/job_trackr-genius/artifacts/eb8bd9q1_Black%20Modern%20A%20letter%20Logo.png" alt="GeniusList" className="w-12 h-12 object-contain" />
            </div>
            <span className="text-xl font-bold text-slate-900">GeniusList</span>
          </div>
          
          {/* Navigation */}
          <div className="px-4 flex-grow">
            <NavContent />
          </div>
          
          {/* User Section */}
          <div className="px-4 mt-auto pt-4 border-t border-slate-100">
            <div className="flex items-center gap-3 px-4 py-3">
              <Avatar className="h-9 w-9">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-purple-100 text-purple-600">
                  {user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-full justify-start text-slate-600 hover:text-rose-600 hover:bg-rose-50 mt-2"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-100">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center">
              <img src="https://customer-assets.emergentagent.com/job_trackr-genius/artifacts/eb8bd9q1_Black%20Modern%20A%20letter%20Logo.png" alt="GeniusList" className="w-10 h-10 object-contain" />
            </div>
            <span className="text-lg font-bold text-slate-900">GeniusList</span>
          </div>
          
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                  <Bell className="w-5 h-5 text-slate-600" />
                  {notifications.length > 0 && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-rose-500 rounded-full"></span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <div className="px-3 py-2 font-medium text-sm">Notifications</div>
                <DropdownMenuSeparator />
                {notifications.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-slate-500 text-center">
                    No new notifications
                  </div>
                ) : (
                  notifications.map((notif, idx) => (
                    <DropdownMenuItem key={idx} className="flex flex-col items-start gap-1">
                      <span className="font-medium">New Referral: {notif.name}</span>
                      <span className="text-xs text-emerald-600">+${notif.commission}</span>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5 text-slate-600" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0">
                <div className="flex flex-col h-full">
                  <div className="flex items-center gap-2 px-6 py-5 border-b border-slate-100">
                    <div className="w-10 h-10 flex items-center justify-center">
                      <img src="https://customer-assets.emergentagent.com/job_trackr-genius/artifacts/eb8bd9q1_Black%20Modern%20A%20letter%20Logo.png" alt="GeniusList" className="w-12 h-12 object-contain" />
                    </div>
                    <span className="text-xl font-bold text-slate-900">GeniusList</span>
                  </div>
                  
                  <div className="px-4 py-4 flex-grow">
                    <NavContent />
                  </div>
                  
                  <div className="px-4 py-4 border-t border-slate-100">
                    <div className="flex items-center gap-3 px-4 py-3">
                      <Avatar className="h-9 w-9">
                        <AvatarImage src={user?.picture} alt={user?.name} />
                        <AvatarFallback className="bg-purple-100 text-purple-600">
                          {user?.name?.charAt(0) || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                        <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      onClick={handleLogout}
                      className="w-full justify-start text-slate-600 hover:text-rose-600 hover:bg-rose-50 mt-2"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="lg:pl-64">
        {/* Desktop Header */}
        <header className="hidden lg:flex items-center justify-between px-8 py-4 bg-white border-b border-slate-100">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {navItems.find(item => item.path === location.pathname)?.label || "Dashboard"}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="desktop-notifications-btn">
                  <Bell className="w-5 h-5 text-slate-600" />
                  {notifications.length > 0 && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-rose-500 rounded-full"></span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <div className="px-3 py-2 font-medium text-sm">Notifications</div>
                <DropdownMenuSeparator />
                {notifications.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-slate-500 text-center">
                    No new notifications
                  </div>
                ) : (
                  notifications.map((notif, idx) => (
                    <DropdownMenuItem key={idx} className="flex flex-col items-start gap-1">
                      <span className="font-medium">New Referral: {notif.name}</span>
                      <span className="text-xs text-emerald-600">+${notif.commission}</span>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-purple-100 text-purple-600">
                      {user?.name?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium text-slate-700">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-rose-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
